import React from "react";
import { noop } from "lodash";

import { useCommonAppDispatcher, useCommonAppSelector } from "../../store/hooks";

interface ErrorBoundaryProps {
  children?: React.ReactNode;
  errorScreen: React.ComponentType<{ onReset: () => void }>;
  onError: (error: Error) => void;
  hasError: boolean;
  setError: (error: { message: string; stack: string }) => void;
}

interface ErrorBoundaryState {
  hasRenderError: boolean;
}

class ErrorBoundaryClass extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state = {
    hasRenderError: false
  };

  public static getDerivedStateFromError(_error: Error) {
    return {
      hasRenderError: true
    };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Create an error object with the component stack
    const errorWithStack: { message: string; stack: string } = {
      message: !error ? "" : typeof error === "string" ? error : error.message ? error.message : "",
      stack: errorInfo.componentStack ?? ""
    };
    this.props.setError(errorWithStack);

    this.props.onError(error);

    this.setState({ hasRenderError: true });
  }

  public render() {
    if (this.state.hasRenderError || this.props.hasError) {
      const ErrorScreen = this.props.errorScreen;

      return (
        <ErrorScreen
          onReset={() => {
            this.setState({ hasRenderError: false });
          }}
        />
      );
    }
    return this.props.children;
  }
}

interface EnhancedErrorBoundaryProps {
  children?: React.ReactNode;
  errorScreen: React.ComponentType<{ onReset: () => void }>;
  onError: (error: Error) => void;
}

const EnhancedErrorBoundary = ({ children, errorScreen, onError }: EnhancedErrorBoundaryProps) => {
  const hasError = useCommonAppSelector(state => state.error.hasError);
  const [dispatch, actions] = useCommonAppDispatcher();

  const setError = (error: { message: string; stack: string }) => dispatch(actions.error.set(error));

  return (
    <ErrorBoundaryClass hasError={hasError} setError={setError} errorScreen={errorScreen} onError={onError}>
      {children}
    </ErrorBoundaryClass>
  );
};

export const errorBoundary =
  (ErrorScreen: React.ComponentType<{ onReset: () => void }>, onError: (error: Error) => void = noop) =>
  (Component: React.ComponentType<any>) => {
    const Wrapped = (props: any) => (
      <EnhancedErrorBoundary errorScreen={ErrorScreen} onError={onError}>
        <Component {...props} />
      </EnhancedErrorBoundary>
    );

    const name = Component.displayName || Component.name;
    Wrapped.displayName = name ? `WithErrorBoundary(${name})` : "WithErrorBoundary";

    return Wrapped;
  };
